import {
  CUSTOMER_PROFILE_OPTIONS,
  DATE_OF_ALLOCATION,
  DPD_BUCKETS_OPTIONS,
  FEEDBACK_TYPES_OPTIONS,
  PAYMENT_STATUS_OPTIONS
} from '../constants/CasesConstants';
import { createSlice } from '@reduxjs/toolkit';
import { ICaseLevelFilter, ICasesState, ILabelValueType } from '../constants/CasesInterfaces';
import { LOCAL_STORAGE_KEYS } from '../../../constants/StorageKeys';
import { AgentTypes } from '../../TeamLeadDashboard/interface';

const initialState = {
  casesTable: {
    filters: {
      schema: {
        paymentStatus: {
          options: PAYMENT_STATUS_OPTIONS,
          multiSelect: true,
          label: 'Payment Status',
          key: 'paymentStatus',
          isMultiSelect: true
        },
        allocatedFieldAgent: {
          options: [],
          multiSelect: true,
          isMultiSelect: true,
          label: 'Allocated FE',
          key: 'allocatedFieldAgent'
        },
        dpdDuringAllocationFilters: {
          options: DPD_BUCKETS_OPTIONS,
          multiSelect: true,
          isMultiSelect: true,
          label: 'DPD At Allocation',
          key: 'dpdDuringAllocationFilters'
        },
        riskSegments: {
          options: CUSTOMER_PROFILE_OPTIONS,
          multiSelect: true,
          isMultiSelect: true,
          label: 'Customer Profile',
          key: 'riskSegments'
        },
        assignedAtFilters: {
          options: DATE_OF_ALLOCATION,
          multiSelect: false,
          label: 'Allocation Date',
          key: 'assignedAtFilters'
        },
        states: {
          options: [],
          multiSelect: true,
          isMultiSelect: true,
          label: "Customer's State",
          key: 'states'
        },
        agentTypes: {
          options: [],
          multiSelect: true,
          isMultiSelect: true,
          label: 'Allocation Purpose',
          key: 'agentTypes'
        },
        teleInteractionStatuses: {
          options: [],
          multiSelect: true,
          isMultiSelect: true,
          label: 'Longhorn Feedback',
          key: 'teleInteractionStatuses'
        },
        fieldInteractionStatuses: {
          options: [],
          multiSelect: true,
          isMultiSelect: true,
          label: 'Cosmos Feedback',
          key: 'fieldInteractionStatuses'
        },
        ptpStatus: {
          options: [],
          multiSelect: false,
          label: 'PTP Status',
          key: 'ptpStatus',
          isMultiSelect: false
        },
        callBackToday: {
          options: [],
          multiSelect: false,
          label: 'Call back today',
          key: 'callBackToday',
          isMultiSelect: false
        },
        callCoverage: {
          options: [],
          multiSelect: false,
          label: 'Call coverage',
          key: 'callCoverage',
          isMultiSelect: false,
          nestedFilter: true
        }
        //TODO: PTP Status filter to be enabled
        // ptpStatus: {
        //   options: [],
        //   multiSelect: true,
        //   label: 'PTP Status',
        //   key: 'ptpStatus',
        //   showResetButton: true,
        //   isMultiSelect: false,
        //   nestedFilter: true
        // },
      }
    },
    pagination: {
      pageNo: 0,
      pageSize: 10,
      totalPages: 0
    },
    sort: {
      sortByField: '',
      sortOrder: ''
    },
    casesData: [],
    nextCasesData: [],
    prevCasesData: [],
    loading: false,
    currentCasePointer: Number(
      localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_CASE_POINTER) || '-1'
    )
  } as ICasesState,
  caseLevelFilters: {} as ICaseLevelFilter,
  pausedCases: []
};

const casesSlice = createSlice({
  name: 'allCases',
  initialState,
  reducers: {
    setSchema(state, action) {
      state.casesTable.filters.schema = { ...state.casesTable.filters.schema, ...action.payload };
    },
    setCasesTableDataAndPagination(state, { payload }) {
      const { responseData } = payload;
      const { caseSummaryPaginatedResponse, featuresInfo, staticFilters, dynamicFilters } =
        responseData;

      if (staticFilters?.assignedAtFilters) {
        state.casesTable.filters.schema.assignedAtFilters.options =
          staticFilters.assignedAtFilters.map((item: any) => {
            return {
              ...item,
              value: item.id
            };
          });
      }
      if (staticFilters?.dpdDuringAllocationFilters) {
        state.casesTable.filters.schema.dpdDuringAllocationFilters.options =
          staticFilters.dpdDuringAllocationFilters;
      }

      if (staticFilters?.riskSegments) {
        state.casesTable.filters.schema.riskSegments.options = staticFilters.riskSegments;
      }

      if (staticFilters?.ptpStatus) {
        state.casesTable.filters.schema.ptpStatus.options = staticFilters?.ptpStatus;
      }

      if (staticFilters?.callBackToday) {
        state.casesTable.filters.schema.callBackToday.options = staticFilters?.callBackToday;
      }

      if (staticFilters?.callCoverage) {
        state.casesTable.filters.schema.callCoverage.options = staticFilters?.callCoverage;
      }

      if (dynamicFilters?.stateFilters) {
        state.casesTable.filters.schema.states.options = dynamicFilters.stateFilters;
      }

      if (staticFilters?.agentTypes) {
        state.casesTable.filters.schema.agentTypes.options =
          staticFilters?.agentTypes?.[0].filterSectionValue.map((item: ILabelValueType) => ({
            ...item,
            label: AgentTypes[item?.value as keyof typeof AgentTypes] || item?.label
          }));
      }

      if (staticFilters?.teleInteractionStatuses) {
        state.casesTable.filters.schema.teleInteractionStatuses.options =
          staticFilters?.teleInteractionStatuses?.[0]?.filterSectionValue ?? [];
      }

      if (staticFilters?.fieldInteractionStatuses) {
        state.casesTable.filters.schema.fieldInteractionStatuses.options =
          staticFilters?.fieldInteractionStatuses?.[0]?.filterSectionValue ?? [];
      }

      if (dynamicFilters?.allocatedFieldAgentFilters) {
        state.casesTable.filters.schema.allocatedFieldAgent.options =
          dynamicFilters?.allocatedFieldAgentFilters;
      }

      state.casesTable.casesData = caseSummaryPaginatedResponse?.data;

      state.casesTable.pagination = {
        ...state.casesTable.pagination,
        ...caseSummaryPaginatedResponse?.pages
      };

      const featuresObj: Record<string, boolean> = {};
      featuresInfo?.forEach((feature: any) => {
        featuresObj[feature?.featureName] = true;
      });

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      state.casesTable.featuresInfo = featuresObj;
      state.casesTable.nextCasesData = [];
      state.casesTable.prevCasesData = [];
      state.casesTable.loading = false;
    },
    setCasesData(state, action) {
      state.casesTable.casesData = { ...state.casesTable.casesData, ...action.payload };
    },
    setPagination(state, action) {
      state.casesTable.pagination = { ...state.casesTable.pagination, ...action.payload };
    },
    setLoading(state, action) {
      state.casesTable.loading = action.payload;
    },
    setCurrentCasePointer(state, action) {
      state.casesTable.currentCasePointer = action.payload;
      localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_CASE_POINTER, action.payload);
    },
    setNextCases(state, { payload }) {
      const { responseData } = payload;
      const { caseSummaryPaginatedResponse } = responseData;
      state.casesTable.nextCasesData = caseSummaryPaginatedResponse?.data;
      if (state.casesTable.loading) {
        state.casesTable.loading = false;
      }
    },
    setPrevCases(state, { payload }) {
      const { responseData } = payload;
      const { caseSummaryPaginatedResponse } = responseData;
      state.casesTable.prevCasesData = caseSummaryPaginatedResponse?.data;
      if (state.casesTable.loading) {
        state.casesTable.loading = false;
      }
    },
    setNextCasesToCurrent(state) {
      state.casesTable.prevCasesData = state.casesTable.casesData;
      state.casesTable.casesData = state.casesTable.nextCasesData;
      state.casesTable.nextCasesData = [];
      state.casesTable.pagination.pageNo = state.casesTable.pagination.pageNo + 1;
      state.casesTable.currentCasePointer = 0;
      localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_CASE_POINTER, '0');
    },
    setPrevCasesToCurrent(state) {
      state.casesTable.nextCasesData = state.casesTable.casesData;
      state.casesTable.casesData = state.casesTable.prevCasesData;
      state.casesTable.prevCasesData = [];
      state.casesTable.pagination.pageNo = state.casesTable.pagination.pageNo - 1;
      state.casesTable.currentCasePointer =
        (state.casesTable.casesData && state.casesTable.casesData.length - 1) || -1;
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.CURRENT_CASE_POINTER,
        `${(state.casesTable.casesData && state.casesTable.casesData.length - 1) || -1}`
      );
    },
    setCaseLevelFiltersLoading(state, action) {
      const key: string = action.payload.key;
      const loading = action.payload.loading;
      state.caseLevelFilters = {
        ...state.caseLevelFilters,
        [key]: {
          ...state.caseLevelFilters[key],
          loading
        }
      };
    },
    setCaseLevelFiltersData(state, action) {
      const key: string = action.payload.key;
      const data = action.payload.data;
      state.caseLevelFilters = {
        ...state.caseLevelFilters,
        [key]: {
          loading: false,
          data
        }
      };
    },
    setPausedCases(state, action) {
      state.pausedCases = action.payload;
    }
  }
});

export const {
  setSchema,
  setCasesTableDataAndPagination,
  setLoading,
  setCurrentCasePointer,
  setNextCases,
  setPagination,
  setNextCasesToCurrent,
  setPrevCasesToCurrent,
  setPrevCases,
  setCaseLevelFiltersLoading,
  setCaseLevelFiltersData,
  setPausedCases
} = casesSlice.actions;

export default casesSlice.reducer;
